/* eslint-disable react/no-danger */
import { GetServerSideProps } from 'next';
import AppContainer from '~/containers/AppContainer/AppContainer';
import { Button, Container } from '~/vinovest-design-system/components';
import React, { useEffect, useRef, useState } from 'react';
import styles from './blog.module.scss';
import { useClientSideNavigation } from '~/hooks/useClientSideNavigation';
import Head from 'next/head';
import { CMSBlogArticle, type CMSBlogImageRendition } from '~/networking/api/cms/responses/Blog';
import { getCMSBlog } from '~/networking/api/cms/requests/getCMSBlog';
import { PosthogHelper } from '~/helpers/PosthogHelper';
import { useGoogleTagManager } from '~/hooks/useGoogleTagManager';
import { PageTitleContext } from '~/hooks/useCustomPageTitle';
import { BackLink } from '~/components/modules/Breadcrumb/BackLink';
import { baseRoutes } from '~/routes/base';
import { useAuthentication } from '~/hooks/useAuthentication';
import { useWindowSize } from '~/hooks/useWindowSize';
import { useTracking } from '~/hooks/tracking/useTracking';
import { AnalyticsEventNames } from '~/tracking';
import { useRouter } from 'next/router';

interface CMSBlogArticlePageProps {
    page: CMSBlogArticle;
}

export const CMSBlogArticlePage = ({ page }: CMSBlogArticlePageProps) => {
    const { isAuthenticated } = useAuthentication();
    const { googleTagManager } = useGoogleTagManager();
    const { isDesktop } = useWindowSize();
    const { captureEvent } = useTracking();
    const [showbanner, setShowBanner] = useState(false);
    const [viewedbanner, setViewedBanner] = useState(false);
    const onClick = useClientSideNavigation();
    const router = useRouter();
    const onetime = useRef(false);

    const handleScroll = () => {
        if (window.scrollY > 400) {
            setShowBanner(true);
        } else {
            setShowBanner(false);
        }
    };

    useEffect(() => {
        if (showbanner && !viewedbanner) {
            captureEvent(AnalyticsEventNames.ViewedBlogSignupBanner);
            setViewedBanner(true);
        }
    }, [showbanner, viewedbanner]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    });

    useEffect(() => {
        // for testing purposes
        void PosthogHelper.init().then((posthog) => {
            window.Vino.posthog = posthog;
        });

        if (!googleTagManager) return;

        PosthogHelper.onFeatureFlag(() => {
            if (PosthogHelper.getFeatureFlag('blog-captain') === 'test' && !onetime.current) {
                void googleTagManager.dispatchEvent('blog-captain');
                onetime.current = true;
            }
        });
    }, [googleTagManager]);

    let largestSize: CMSBlogImageRendition = { url: '', width: 0, height: 0 };
    const preloadSizes = Object.values(page.hero_image).map((value) => {
        if (value.width > largestSize.width) {
            largestSize = value;
        }
        return `${value.url} ${value.width}w`;
    });

    const handleClick = () => {
        captureEvent(AnalyticsEventNames.ClickBlogSignupBanner);
        router.push(baseRoutes.signup.href);
    };

    // the first instance should be the hero
    const body = page.body.replace('loading="lazy"', 'loading="eager"');

    return (
        <PageTitleContext.Provider value={{ pageTitle: page.title, backTo: '/blog' }}>
            <AppContainer>
                <Head>
                    <title>{page.title}</title>
                    <meta name={'description'} content={page.meta_preview_text} />
                    <link rel={'canonical'} href={`https://www.vinovest.co/blog/${page.slug}`} />
                    <link
                        rel={'preload'}
                        as={'image'}
                        href={largestSize.url}
                        imageSrcSet={preloadSizes.join(', ')}
                        imageSizes={'(max-width:1024px) 93vw, 1028px'}
                    />
                </Head>
                {!isAuthenticated && (
                    <div
                        className={`bg-platinum-850 fixed w-full px-12 md:px-4 py-6 md:py-8 z-50 ${showbanner ? 'bottom-0 opacity-100' : 'bottom-[-100%] opacity-0'} transition-all duration-1000`}
                    >
                        <div
                            className={'max-w-[1024px] mx-auto flex items-center justify-between flex-col md:flex-row'}
                        >
                            <div>
                                <h2
                                    className={
                                        'text-center md:text-left text-28 md:text-36 font-roslindale text-brown-300 mb-4'
                                    }
                                >
                                    Ready to Invest Smarter?
                                </h2>
                                <p
                                    className={
                                        'text-16 md:text-18 text-brown-300 mb-4 md:mb-2 text-center md:text-left'
                                    }
                                >
                                    Fine wine and whiskey don't just age well—they build wealth.
                                </p>
                                {isDesktop && (
                                    <p className={'text-16 md:text-18 text-brown-300 mb-6 md:mb-0'}>
                                        Join thousands of investors diversifying with real, appreciating assets.
                                    </p>
                                )}
                            </div>
                            <div>
                                <Button
                                    href={baseRoutes.signup.href}
                                    variant={'green'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleClick();
                                    }}
                                    classnames={'md:text-18'}
                                >
                                    Start your portfolio today
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                <Container classnames={'sm:max-w-[93%] lg:max-w-[1135px]'} maxWidth={'max-w-full md:container'}>
                    <div className={'md:mt-20 w-full mb-32'}>
                        <BackLink
                            maxWidth={'max-w-[900px]'}
                            classnames={'mb-[20px] mt-[20px] md:mt-[-40px]'}
                            previousRoute={baseRoutes.dashboard.href}
                        />
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                        <div
                            onClick={onClick}
                            className={styles.container}
                            dangerouslySetInnerHTML={{ __html: body }}
                        />
                    </div>
                </Container>
            </AppContainer>
        </PageTitleContext.Provider>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
    if (params?.slug === undefined || typeof params.slug !== 'string') {
        return { notFound: true };
    }

    const response = await getCMSBlog(params.slug);
    if (response.status !== 200) {
        if (response.status !== 404) {
            console.error('Failed to fetch Blog page', response);
        }
        return { notFound: true };
    }

    return {
        props: {
            page: response.data
        }
    };
};

export default CMSBlogArticlePage;
